import { useEffect, useState } from "react";
import { api } from "./lib/api";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Trophy, Megaphone, SplitSquareVertical, Inbox, Mail, Settings as SettingsIcon,
  RefreshCw, ShieldCheck, Activity, ChevronRight, LogOut, Users,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import SalesPerformance from "./pages/SalesPerformance";
import MarketingLeads from "./pages/MarketingLeads";
import Reconciliation from "./pages/Reconciliation";
import IntakeReview from "./pages/IntakeReview";
import Recap from "./pages/Recap";
import SettingsPage from "./pages/Settings";
import RepMapping from "./pages/RepMapping";
import Login from "./pages/Login";
import { AppFiltersProvider } from "./state/filters";
import { AuthProvider, useAuth } from "./state/auth";
import DateRangePicker from "./components/DateRangePicker";
import FilterBar from "./components/FilterBar";
import "./App.css";

const NAV = [
  { to: "/", label: "Executive", icon: LayoutDashboard, roles: ["admin", "manager", "viewer"] },
  { to: "/sales", label: "Sales Performance", icon: Trophy, roles: ["admin", "manager", "rep", "viewer"] },
  { to: "/reps", label: "Rep Mapping", icon: Users, roles: ["admin", "manager"] },
  { to: "/marketing", label: "Marketing & Leads", icon: Megaphone, roles: ["admin", "manager", "viewer"] },
  { to: "/reconciliation", label: "Reconciliation", icon: SplitSquareVertical, roles: ["admin", "manager", "viewer"] },
  { to: "/intake", label: "Intake Review", icon: Inbox, roles: ["admin", "manager", "viewer"] },
  { to: "/recap", label: "Recap Emails", icon: Mail, roles: ["admin", "manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin"] },
];

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function Sidebar({ integrations, health, user, onLogout }) {
  const allowed = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  return (
    <aside className="w-[260px] shrink-0 border-r border-border bg-[hsl(var(--surface-1))] flex flex-col" data-testid="app-sidebar">
      <div className="h-14 border-b border-border px-5 flex items-center gap-3">
        <div className="size-8 rounded-md bg-[hsl(var(--positive))]/15 border border-[hsl(var(--positive))]/30 grid place-items-center">
          <ShieldCheck size={18} className="text-[hsl(var(--positive))]" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-[15px] font-semibold">Coastal Wildlife</div>
          <div className="text-[11px] text-muted-foreground tracking-wide uppercase">Command Center</div>
        </div>
      </div>
      <nav className="py-3 flex-1">
        {allowed.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-lg text-sm transition-[background-color,color] ${
                  isActive
                    ? "bg-[hsl(var(--surface-2))] text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/60"
                }`
              }
            >
              <Icon size={18} strokeWidth={1.75} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border space-y-2">
        <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))]/60 p-3" data-testid="integration-status-card">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Integrations</div>
          <div className="mt-2 space-y-1.5 text-xs">
            <IntegrationRow name="GoHighLevel" status={integrations?.ghl?.status} count={integrations?.ghl?.records} />
            <IntegrationRow name="FieldRoutes" status={integrations?.fieldroutes?.status} count={integrations?.fieldroutes?.records} mode={integrations?.fieldroutes?.mode} />
            <IntegrationRow name="Resend" status={integrations?.resend?.status} />
          </div>
        </div>
        <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))]/60 p-3 flex items-center gap-3" data-testid="data-health-badge">
          <div className={`size-9 rounded-md grid place-items-center border ${health >= 80 ? "border-[hsl(var(--positive))]/40 bg-[hsl(var(--positive))]/15" : health >= 60 ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/15" : "border-[hsl(var(--negative))]/40 bg-[hsl(var(--negative))]/15"}`}>
            <Activity size={18} className={health >= 80 ? "text-[hsl(var(--positive))]" : health >= 60 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--negative))]"} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Data Health</div>
            <div className="font-display text-lg font-semibold leading-none mt-1 tabular-nums" data-testid="data-health-score">{health ?? 0}</div>
          </div>
        </div>
        {user && (
          <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))]/60 p-3 flex items-center justify-between gap-2" data-testid="user-card">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{user.role}</div>
            </div>
            <Button size="icon" variant="ghost" onClick={onLogout} data-testid="logout-button"><LogOut size={14} /></Button>
          </div>
        )}
      </div>
    </aside>
  );
}

function IntegrationRow({ name, status, count, mode }) {
  let color = "text-muted-foreground bg-muted border-border";
  let label = "Pending";
  if (status === "live") { color = "text-[hsl(var(--positive))] bg-[hsl(var(--positive))]/15 border-[hsl(var(--positive))]/30"; label = "Live"; }
  else if (status === "pending_credentials") { color = "text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/15 border-[hsl(var(--warning))]/30"; label = "Pending"; }
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-foreground">{name}</span>
        {count !== undefined && <span className="text-[10px] text-muted-foreground tabular-nums">{count?.toLocaleString?.() ?? count}</span>}
      </div>
      <span className={`px-1.5 py-0.5 rounded-full text-[10px] border ${color}`}>{mode === "seeded" ? "Seeded" : label}</span>
    </div>
  );
}

function TopBar({ onSync, syncing, lastSync }) {
  const loc = useLocation();
  const title = NAV.find(n => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)))?.label || "Coastal Wildlife";
  const { hasRole } = useAuth();
  return (
    <div className="h-14 border-b border-border bg-[hsl(var(--surface-1))]/80 backdrop-blur sticky top-0 z-20 flex items-center px-6 gap-4" data-testid="app-topbar">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Command Center</span>
        <ChevronRight size={14} />
        <span className="text-foreground font-medium">{title}</span>
      </div>
      <div className="flex-1" />
      <FilterBar />
      <DateRangePicker />
      <div className="text-[11px] text-muted-foreground hidden md:block" data-testid="last-sync-label">
        {lastSync ? `Last sync ${new Date(lastSync).toLocaleTimeString()}` : "Not synced yet"}
      </div>
      {hasRole("admin", "manager") && (
        <Button size="sm" onClick={onSync} disabled={syncing} data-testid="sync-now-button">
          <RefreshCw size={14} className={syncing ? "animate-spin mr-2" : "mr-2"} />
          {syncing ? "Syncing" : "Sync Now"}
        </Button>
      )}
    </div>
  );
}

function Shell() {
  const { user, logout, hasRole } = useAuth();
  const nav = useNavigate();
  const [integrations, setIntegrations] = useState(null);
  const [health, setHealth] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadStatus = async () => {
    try {
      const [ints, summ] = await Promise.all([
        api.get("/status/integrations"),
        api.get("/reconciliation/summary"),
      ]);
      setIntegrations(ints.data);
      setHealth(summ.data?.health?.health_score ?? 0);
    } catch { /* ignore */ }
    try {
      const logs = await api.get("/sync/logs", { params: { limit: 1 } });
      if (logs.data?.logs?.[0]?.completed_at) setLastSync(logs.data.logs[0].completed_at);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    loadStatus();
    const t = setInterval(loadStatus, 60000);
    return () => clearInterval(t);
  }, [user]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/ghl", null, { params: { full: false } });
      if (data.already_running) {
        toast.message("Sync already in progress");
      } else {
        toast.success("Sync started in background");
      }
      // Poll sync status every 4s; stop polling when no sync is running
      let pollTimer = null;
      const poll = async () => {
        try {
          const s = await api.get("/sync/status");
          if (!s.data?.any_running) {
            await loadStatus();
            setSyncing(false);
            window.dispatchEvent(new Event("cwcc:refresh"));
            if (pollTimer) clearInterval(pollTimer);
          }
        } catch { /* ignore transient errors */ }
      };
      pollTimer = setInterval(poll, 4000);
      // safety stop after 3 minutes
      setTimeout(() => {
        if (pollTimer) clearInterval(pollTimer);
        setSyncing(false);
        loadStatus();
      }, 180000);
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
      setSyncing(false);
    }
  };

  const handleLogout = () => { logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen flex app-noise" data-testid="app-shell">
      <Sidebar integrations={integrations} health={health} user={user} onLogout={handleLogout} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onSync={handleSync} syncing={syncing} lastSync={lastSync} />
        <main className="flex-1 px-6 py-6 overflow-x-hidden">
          <Routes>
            <Route path="/" element={hasRole("admin", "manager", "viewer") ? <ExecutiveDashboard /> : <Navigate to="/sales" replace />} />
            <Route path="/sales" element={<SalesPerformance />} />
            <Route path="/reps" element={hasRole("admin", "manager") ? <RepMapping /> : <Navigate to="/" replace />} />
            <Route path="/marketing" element={hasRole("admin", "manager", "viewer") ? <MarketingLeads /> : <Navigate to="/" replace />} />
            <Route path="/reconciliation" element={hasRole("admin", "manager", "viewer") ? <Reconciliation /> : <Navigate to="/" replace />} />
            <Route path="/intake" element={hasRole("admin", "manager", "viewer") ? <IntakeReview /> : <Navigate to="/" replace />} />
            <Route path="/recap" element={hasRole("admin", "manager") ? <Recap /> : <Navigate to="/" replace />} />
            <Route path="/settings" element={hasRole("admin") ? <SettingsPage /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppFiltersProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<RequireAuth><Shell /></RequireAuth>} />
          </Routes>
        </AppFiltersProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
